import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {getBlogPost, getPublishedPosts} from '../utils/blogStorage';
import {getAuthor} from '../utils/authorStorage';

const {FiArrowLeft, FiCalendar, FiUser, FiClock, FiTag, FiShare2, FiBookmark, FiEye, FiHeart, FiTwitter, FiLinkedin, FiFacebook, FiLink, FiMapPin, FiGlobe, FiMail, FiFileText} = FiIcons;

const BlogPost = () => {
  const {id} = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [author, setAuthor] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [readingProgress, setReadingProgress] = useState(0);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  useEffect(() => {
    loadPost();
    setShareUrl(window.location.href);
    
    // Track reading progress
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setReadingProgress(Math.min(progress, 100));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [id]);

  const loadPost = async () => {
    try {
      setIsLoading(true);
      const blogPost = await getBlogPost(id);
      
      if (!blogPost || blogPost.status !== 'published') {
        navigate('/blog');
        return;
      }

      setPost(blogPost);

      // Load author information
      if (blogPost.author) {
        const authorData = await getAuthor(blogPost.author);
        setAuthor(authorData);
      }

      // Load related posts
      const allPosts = await getPublishedPosts();
      const related = allPosts
        .filter(p => p.id !== id && p.category === blogPost.category)
        .slice(0, 3);
      setRelatedPosts(related);

      // Check bookmarks and likes from localStorage
      const bookmarks = JSON.parse(localStorage.getItem('bookmarked_posts') || '[]');
      const likes = JSON.parse(localStorage.getItem('liked_posts') || '[]');
      setIsBookmarked(bookmarks.includes(id));
      setIsLiked(likes.includes(id));

    } catch (error) {
      console.error('Error loading blog post:', error);
      navigate('/blog');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBookmark = () => {
    const bookmarks = JSON.parse(localStorage.getItem('bookmarked_posts') || '[]');
    let newBookmarks;
    
    if (isBookmarked) {
      newBookmarks = bookmarks.filter(postId => postId !== id);
    } else {
      newBookmarks = [...bookmarks, id];
    }
    
    localStorage.setItem('bookmarked_posts', JSON.stringify(newBookmarks));
    setIsBookmarked(!isBookmarked);
  };

  const handleLike = () => {
    const likes = JSON.parse(localStorage.getItem('liked_posts') || '[]');
    let newLikes;
    
    if (isLiked) {
      newLikes = likes.filter(postId => postId !== id);
    } else {
      newLikes = [...likes, id];
    }
    
    localStorage.setItem('liked_posts', JSON.stringify(newLikes));
    setIsLiked(!isLiked);
  };

  const handleShare = (platform) => {
    const text = `Check out this article: ${post.title}`;
    const url = shareUrl;

    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        // Show toast notification
        const toast = document.createElement('div');
        toast.className = 'fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50';
        toast.textContent = 'Link copied to clipboard!';
        document.body.appendChild(toast);
        setTimeout(() => document.body.removeChild(toast), 3000);
        break;
    }
  };

  const formatContent = (content) => {
    if (!content) return '';
    
    // Convert markdown-like syntax to HTML
    let formattedContent = content
      // Headers
      .replace(/^### (.*$)/gim, '<h3 class="text-2xl font-bold text-gray-900 mt-8 mb-4">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-3xl font-bold text-gray-900 mt-10 mb-6">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-4xl font-bold text-gray-900 mt-12 mb-8">$1</h1>')
      // Bold and italic
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
      // Code blocks
      .replace(/```([\s\S]*?)```/g, '<pre class="bg-gray-100 border border-gray-300 rounded-lg p-4 my-6 overflow-x-auto"><code class="text-sm text-gray-800">$1</code></pre>')
      .replace(/`([^`]+)`/g, '<code class="bg-gray-100 text-gray-800 px-2 py-1 rounded text-sm">$1</code>')
      // Lists
      .replace(/^\* (.*$)/gim, '<li class="ml-6 mb-2">• $1</li>')
      .replace(/^- (.*$)/gim, '<li class="ml-6 mb-2">• $1</li>')
      // Paragraphs
      .replace(/\n\n/g, '</p><p class="text-gray-700 leading-relaxed mb-6">')
      // Line breaks
      .replace(/\n/g, '<br>');

    // Wrap in paragraph tags if not already wrapped
    if (!formattedContent.includes('<p>') && !formattedContent.includes('<h')) {
      formattedContent = `<p class="text-gray-700 leading-relaxed mb-6">${formattedContent}</p>`;
    } else if (formattedContent.includes('<li>')) {
      formattedContent = formattedContent.replace(/(<li>.*?<\/li>)/gs, '<ul class="list-none mb-6">$1</ul>');
    }

    return formattedContent;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <motion.div
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          className="text-center"
        >
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading article...</p>
        </motion.div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Article Not Found</h2>
          <p className="text-gray-600 mb-6">The article you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/blog')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Blog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-white">
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-50">
        <motion.div
          className="h-full bg-gradient-to-r from-blue-600 to-purple-600"
          style={{width: `${readingProgress}%`}}
          transition={{duration: 0.1}}
        />
      </div>

      {/* Navigation */}
      <div className="sticky top-16 bg-white/95 backdrop-blur-md border-b border-gray-200 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/blog')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="h-5 w-5 mr-2" />
              Back to Blog
            </button>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handleLike}
                className={`p-2 rounded-full transition-colors ${
                  isLiked ? 'text-red-600 bg-red-50' : 'text-gray-600 hover:text-red-600 hover:bg-red-50'
                }`}
              >
                <SafeIcon icon={FiHeart} className="h-5 w-5" />
              </button>
              
              <button
                onClick={handleBookmark}
                className={`p-2 rounded-full transition-colors ${
                  isBookmarked ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <SafeIcon icon={FiBookmark} className="h-5 w-5" />
              </button>

              <div className="relative group">
                <button className="p-2 rounded-full text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                  <SafeIcon icon={FiShare2} className="h-5 w-5" />
                </button>
                
                {/* Share Dropdown - Fixed alignment */}
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="p-2">
                    {[
                      {platform: 'twitter', icon: FiTwitter, label: 'Share on Twitter', color: 'hover:text-blue-400'},
                      {platform: 'linkedin', icon: FiLinkedin, label: 'Share on LinkedIn', color: 'hover:text-blue-600'},
                      {platform: 'facebook', icon: FiFacebook, label: 'Share on Facebook', color: 'hover:text-blue-700'},
                      {platform: 'copy', icon: FiLink, label: 'Copy Link', color: 'hover:text-gray-700'}
                    ].map((item) => (
                      <button
                        key={item.platform}
                        onClick={() => handleShare(item.platform)}
                        className={`w-full flex items-center px-3 py-2 text-sm text-gray-700 ${item.color} hover:bg-gray-50 rounded transition-colors text-left`}
                      >
                        <SafeIcon icon={item.icon} className="h-4 w-4 mr-3 flex-shrink-0" />
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.header
          initial={{opacity: 0, y: 20}}
          animate={{opacity: 1, y: 0}}
          className="mb-12"
        >
          <div className="flex items-center mb-6">
            <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
              {post.category}
            </span>
            {post.featured && (
              <span className="bg-yellow-100 text-yellow-800 text-sm font-medium px-3 py-1 rounded-full ml-3">
                Featured
              </span>
            )}
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
            {post.title}
          </h1>

          <p className="text-xl text-gray-600 leading-relaxed mb-8">
            {post.excerpt}
          </p>

          <div className="flex items-center justify-between border-b border-gray-200 pb-6">
            <div className="flex items-center space-x-6 text-sm text-gray-600">
              <div className="flex items-center">
                <SafeIcon icon={FiUser} className="h-4 w-4 mr-2" />
                <span className="font-medium">{post.author}</span>
              </div>
              <div className="flex items-center">
                <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2" />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center">
                <SafeIcon icon={FiClock} className="h-4 w-4 mr-2" />
                <span>{post.readTime}</span>
              </div>
            </div>
          </div>
        </motion.header>

        {/* Featured Image */}
        {post.image && (
          <motion.div
            initial={{opacity: 0, scale: 0.95}}
            animate={{opacity: 1, scale: 1}}
            transition={{delay: 0.2}}
            className="mb-12"
          >
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-64 md:h-96 object-cover rounded-2xl shadow-lg"
            />
          </motion.div>
        )}

        {/* Article Body */}
        <motion.div
          initial={{opacity: 0, y: 20}}
          animate={{opacity: 1, y: 0}}
          transition={{delay: 0.3}}
          className="prose prose-lg max-w-none mb-12"
          dangerouslySetInnerHTML={{__html: formatContent(post.content)}}
        />

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <motion.div
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{delay: 0.4}}
            className="mb-12"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <SafeIcon icon={FiTag} className="h-5 w-5 mr-2" />
              Tags
            </h3>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, index) => (
                <span
                  key={index}
                  className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm hover:bg-gray-200 transition-colors cursor-pointer"
                >
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>
        )}

        {/* Enhanced Author Bio */}
        <motion.div
          initial={{opacity: 0, y: 20}}
          animate={{opacity: 1, y: 0}}
          transition={{delay: 0.5}}
          className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 mb-12 border border-gray-200"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">About the Author</h3>
          <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-6">
            {/* Author Avatar */}
            <div className="flex-shrink-0">
              {author?.avatar ? (
                <img
                  src={author.avatar}
                  alt={author.name}
                  className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
                />
              ) : (
                <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center border-4 border-white shadow-lg">
                  <span className="text-white font-bold text-2xl">
                    {post.author?.charAt(0) || 'A'}
                  </span>
                </div>
              )}
            </div>
            
            {/* Author Info */}
            <div className="flex-1">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3">
                <h4 className="font-bold text-xl text-gray-900 mb-1">{post.author}</h4>
                {author?.social && (
                  <div className="flex items-center space-x-3">
                    {author.social.twitter && (
                      <a
                        href={author.social.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-600 transition-colors"
                      >
                        <SafeIcon icon={FiTwitter} className="h-4 w-4" />
                      </a>
                    )}
                    {author?.social.linkedin && (
                      <a
                        href={author.social.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 transition-colors"
                      >
                        <SafeIcon icon={FiLinkedin} className="h-4 w-4" />
                      </a>
                    )}
                    {author?.website && (
                      <a
                        href={author.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        <SafeIcon icon={FiGlobe} className="h-4 w-4" />
                      </a>
                    )}
                    {author?.email && (
                      <a
                        href={`mailto:${author.email}`}
                        className="text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        <SafeIcon icon={FiMail} className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                )}
              </div>
              
              {author?.title && (
                <p className="text-blue-600 font-medium mb-2">{author.title}</p>
              )}
              
              <p className="text-gray-600 text-sm leading-relaxed mb-3">
                {author?.bio || 'Expert in AI-powered development and software engineering. Passionate about sharing insights on modern development practices and emerging technologies.'}
              </p>
              
              {/* Author Stats */}
              <div className="flex items-center space-x-6 text-xs text-gray-500">
                {author?.location && (
                  <div className="flex items-center">
                    <SafeIcon icon={FiMapPin} className="h-3 w-3 mr-1" />
                    <span>{author.location}</span>
                  </div>
                )}
                {author?.yearsExperience && (
                  <div className="flex items-center">
                    <SafeIcon icon={FiUser} className="h-3 w-3 mr-1" />
                    <span>{author.yearsExperience} years experience</span>
                  </div>
                )}
                {author?.articlesCount && (
                  <div className="flex items-center">
                    <SafeIcon icon={FiFileText} className="h-3 w-3 mr-1" />
                    <span>{author.articlesCount} articles</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{opacity: 0, y: 20}}
              whileInView={{opacity: 1, y: 0}}
              viewport={{once: true}}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Related Articles</h2>
              <p className="text-gray-600">Continue exploring our insights</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost, index) => (
                <motion.article
                  key={relatedPost.id}
                  initial={{opacity: 0, y: 20}}
                  whileInView={{opacity: 1, y: 0}}
                  viewport={{once: true}}
                  transition={{delay: index * 0.1}}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:scale-105 cursor-pointer group"
                  onClick={() => navigate(`/blog/${relatedPost.id}`)}
                >
                  <img
                    src={relatedPost.image || 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400&h=300&fit=crop'}
                    alt={relatedPost.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="p-6">
                    <div className="flex items-center mb-3">
                      <SafeIcon icon={FiTag} className="h-4 w-4 text-blue-600 mr-2" />
                      <span className="text-blue-600 text-sm font-medium">{relatedPost.category}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h3>
                    <p className="text-gray-600 mb-4 leading-relaxed line-clamp-3">
                      {relatedPost.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center">
                        <SafeIcon icon={FiUser} className="h-4 w-4 mr-1" />
                        {relatedPost.author}
                      </div>
                      <span>{relatedPost.readTime}</span>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{opacity: 0, y: 40}}
            whileInView={{opacity: 1, y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.8}}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Development Process?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Let's discuss how our AI-accelerated solutions can bring your vision to life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/contact')}
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                Start Your Project
              </button>
              <button
                onClick={() => navigate('/blog')}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300"
              >
                Read More Articles
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;